import { Link } from "react-router-dom";
import { hotEvents } from "@/mocks/events";
import { siteMeta } from "@/mocks/site";
import { formatHeat } from "@/utils/format";

export default function Hero() {
  const top5 = hotEvents.slice(0, 5);
  const maxHeat = top5[0]?.heat || 1;

  return (
    <section id="top" className="relative overflow-hidden bg-background-50 pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="absolute inset-0 net-grid opacity-60" />
      <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-primary-100/60 blur-3xl" />
      <div className="absolute top-40 -left-32 w-[420px] h-[420px] rounded-full bg-accent-100/50 blur-3xl" />

      <div className="relative w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200 text-xs font-label text-primary-700">
              <span className="relative w-2 h-2 flex items-center justify-center">
                <span className="absolute inset-0 rounded-full bg-primary-500 animate-pulse-ring" />
                <span className="w-2 h-2 rounded-full bg-primary-500" />
              </span>
              实时追踪中 · 每小时刷新热度序列
            </span>

            <h1 className="mt-6 font-heading text-3xl md:text-5xl xl:text-6xl font-black text-foreground-950 leading-[1.15] tracking-tight">
              网红黑曝
              <span className="block mt-2 text-2xl md:text-4xl xl:text-[2.75rem] font-bold text-foreground-800">
                是怎么一步步发酵、扩散、上热搜的？
              </span>
            </h1>

            <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              {siteMeta.brand}聚焦{siteMeta.tagline}，把一条爆料的
              <strong className="text-foreground-900 font-normal">首爆时间、话题量变化、转发增速与扩散渠道</strong>
              整理成可对比的曲线。不判断是非，只把事件热度走势与扩散节点讲清楚。
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#ranking"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label whitespace-nowrap transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-fire-line" />
                </span>
                查看实时热搜追踪榜
              </a>
              <Link
                to="/events"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-background-100 hover:bg-background-200 border border-background-300 text-foreground-900 text-sm font-label whitespace-nowrap transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-list-check-2" />
                </span>
                浏览事件追踪库
              </Link>
            </div>

            <dl className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { k: "在追话题", v: "1,286" },
                { k: "今日新增爆料", v: "73" },
                { k: "平均发酵时长", v: "4.6h" },
                { k: "覆盖平台", v: "12" },
              ].map((item) => (
                <div key={item.k} className="border-l-2 border-primary-200 pl-3">
                  <dt className="text-xs text-foreground-500">{item.k}</dt>
                  <dd className="mt-1 font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    {item.v}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-primary-100/70 via-background-100/40 to-accent-100/60" />
              <div className="relative rounded-2xl bg-background-50 border border-background-200 p-5 md:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
                      <i className="ri-radar-line text-base" />
                    </span>
                    <div className="flex flex-col leading-none">
                      <span className="font-heading text-sm font-bold text-foreground-950">热度雷达</span>
                      <span className="text-[11px] text-foreground-500 mt-1">TOP 5 · 实时观测</span>
                    </div>
                  </div>
                  <span className="text-[11px] font-label text-secondary-700 bg-secondary-50 px-2 py-1 rounded-md">
                    更新于 5 分钟前
                  </span>
                </div>

                <ul className="mt-5 space-y-3.5">
                  {top5.map((event, index) => (
                    <li key={event.id}>
                      <Link to={`/event/${event.id}`} className="group block">
                        <div className="flex items-center gap-3">
                          <span
                            className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center text-xs font-label ${
                              index === 0
                                ? "bg-primary-500 text-background-50"
                                : index === 1
                                  ? "bg-primary-100 text-primary-700"
                                  : "bg-background-200 text-foreground-600"
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="flex-1 truncate text-sm text-foreground-800 group-hover:text-primary-600 transition-colors">
                            {event.title}
                          </span>
                          <span className="font-label text-xs text-primary-600 shrink-0">
                            {formatHeat(event.heat)}
                          </span>
                        </div>
                        <div className="mt-2 ml-9 h-1.5 rounded-full bg-background-200 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-600"
                            style={{ width: `${Math.round((event.heat / maxHeat) * 100)}%` }}
                          />
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>

                <a
                  href="#ranking"
                  className="mt-5 flex items-center justify-center gap-1.5 h-10 rounded-md bg-background-100 hover:bg-background-200 text-sm font-label text-foreground-800 transition-colors cursor-pointer"
                >
                  查看完整榜单
                  <i className="ri-arrow-down-line" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}